import { fetchWithAuth } from './fetchWithAuth';

/**
 * Stream chat retrieval from Werner backend
 */
export async function streamChatRetrieval({
  question,
  conversationHistory = [],
  apiBase,
  redisUrl,
  indexName = null,
  chatId = null,
  onStatusUpdate = () => { },
  onTextChunk = () => { },
  onComplete = () => { },
  onError = () => { }
}) {
  const filters = { filter_conditions: [] };
  if (indexName) filters.index_name = indexName;

  const payload = {
    data: {
      type: "werner",
      attributes: {
        question,
        redis_url: redisUrl,
        filters,
        // identifies the conversation so the backend can persist/restore agent
        // state (thread_id) across turns
        ...(chatId != null ? { chat_id: chatId } : {}),
        "past-conversations": conversationHistory
          .filter(m => m.sender !== "ai" || m.text !== "Hello! How can I help you today?")
          .map(m => {
            if (m.sender === "human") return { role: "user", content: m.text };
            return { role: "assistant", content: m.text };
          }),
      },
    },
  };

  try {
    const response = await fetchWithAuth(`${apiBase}/ainstein/chat-retrieval`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        "Cache-Control": "no-cache"
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const textDecoder = new TextDecoder();

    let streamedText = "";
    let buffer = "";

    console.log('Beginning to read stream...');
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += textDecoder.decode(value).replace(/\r/g, '');

      let sepIndex;
      while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
        const eventBlock = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);

        let eventType = null;
        const dataLines = [];

        for (const line of eventBlock.split("\n")) {
          if (!line) continue;
          if (line.startsWith(":")) continue;

          const colonIdx = line.indexOf(":");
          const field = colonIdx !== -1 ? line.slice(0, colonIdx) : line;
          const valueStr = colonIdx !== -1 ? line.slice(colonIdx + 1).trimStart() : "";

          if (field === "event") eventType = valueStr || null;
          if (field === "data") dataLines.push(valueStr);
        }

        const dataStr = dataLines.join("\n");
        if (!dataStr) continue;

        let parsed = null;
        try {
          parsed = JSON.parse(dataStr);
        } catch {
          // If JSON fails AND there’s no event type, treat it as raw text chunk
          if (!eventType) {
            streamedText += dataStr;
            onTextChunk(streamedText);
          }
          continue;
        }

        const type = eventType || parsed?.type || null;

        switch (type) {
          case 'status': {
            const message = parsed?.message ?? parsed?.data ?? dataStr;
            onStatusUpdate(message);
            break;
          }
          case 'chunk': {
            const textChunk = parsed?.content ?? parsed?.data ?? "";
            streamedText += textChunk;
            onTextChunk(streamedText);
            break;
          }
          case "answer": {
            console.log("ANSWER EVENT RAW:", parsed);

            const citations = parsed?.Links ?? parsed?.links ?? [];
            const functions = parsed?.functions ?? [];

            console.log("ANSWER EVENT FUNCTIONS:", functions);

            onComplete({
              text: streamedText,
              citations: Array.isArray(citations) ? citations : [],
              functions: Array.isArray(functions) ? functions : [],
            });

            console.log("Streaming complete.");
            return;
          }
          
          default: {
            // Unknown type: attempt to treat it as text
            const textChunk = parsed?.data ?? "";
            streamedText += textChunk;
            onTextChunk(streamedText);
            break;
          }
        }
      }
    }

    console.log('Stopped the streaming loop.');
  } catch (err) {
    console.error('Chat streaming error:', err);
    onError(err);
  }
}
